/**
 * Утилиты для работы с курсорами пагинации
 */

/**
 * Проверяет, валиден ли курсор
 * @param {string} cursor - Курсор для проверки
 * @returns {boolean} - Валиден ли курсор
 */
export function isValidCursor(cursor) {
    return cursor && typeof cursor === 'string' && cursor.trim().length > 0;
}

/**
 * Создает объект параметров для запроса с курсором
 * @param {Object} options - Опции запроса
 * @param {string} options.cursor - Курсор для пагинации
 * @param {number} options.limit - Лимит элементов
 * @returns {Object} - Параметры для запроса
 */
export function createCursorParams(options = {}) {
    const params = {};

    if (options.limit) {
        params.limit = options.limit;
    }

    if (isValidCursor(options.cursor)) {
        params.cursor = options.cursor;
    }

    return params;
}

/**
 * Обрабатывает ответ API с пагинацией
 * @param {Object} response - Ответ от API
 * @returns {Object} - Обработанные данные пагинации
 */
export function processPaginationResponse(response) {
    const data = response?.data || {};
    
    const nextCursor = data.next_cursor;
    const hasMore = data.has_more || false;

    const processedPagination = {
        next_cursor: nextCursor?.last_id || null,
        prev_cursor: null,
        has_next: hasMore && nextCursor !== null,
        has_prev: false,
        current_cursor: null,
        total_count: data.total_count || 0,
        current_page: data.current_page || 1,
        total_pages: data.total_pages || 1,
    };

    return processedPagination;
}

/**
 * Проверяет, можно ли перейти на следующую страницу
 * @param {Object} pagination - Объект пагинации
 * @returns {boolean} - Можно ли перейти дальше
 */
export function canGoNext(pagination) {
    const canGo = pagination.has_next && isValidCursor(pagination.next_cursor);
    return canGo;
}

/**
 * Проверяет, можно ли перейти на предыдущую страницу
 * @param {Object} pagination - Объект пагинации
 * @returns {boolean} - Можно ли перейти назад
 */
export function canGoPrev(pagination) {
    // Если has_prev = false, то предыдущей страницы точно нет
    if (!pagination.has_prev) {
        return false;
    }

    // Если has_prev = true, но prev_cursor отсутствует,
    // то это может быть ошибка на бэкенде или первая страница
    const canGo = isValidCursor(pagination.prev_cursor);
    return canGo;
}

/**
 * Создает объект состояния пагинации
 * @param {Object} pagination - Данные пагинации от API
 * @param {string} currentCursor - Текущий курсор
 * @returns {Object} - Объект состояния пагинации
 */
export function createPaginationState(pagination, currentCursor = null) {
    const result = {
        next_cursor: pagination.next_cursor || null,
        prev_cursor: pagination.prev_cursor || null,
        has_next: pagination.has_next || false,
        has_prev: pagination.has_prev || false,
        current_cursor: currentCursor,
    };

    return result;
}

/**
 * Очищает параметры пагинации из URL
 * @param {Object} query - Объект query из URL
 * @returns {Object} - Очищенный объект query
 */
export function clearPaginationFromQuery(query) {
    const cleanedQuery = {...query};

    delete cleanedQuery.cursor;
    delete cleanedQuery.next_cursor;
    delete cleanedQuery.prev_cursor;
    delete cleanedQuery.current_cursor;
    delete cleanedQuery.page_id;
    // НЕ удаляем limit, так как это не параметр пагинации

    return cleanedQuery;
}

/**
 * Добавляет курсор в URL query
 * @param {Object} query - Объект query из URL
 * @param {string} cursor - Курсор для добавления
 * @returns {Object} - Обновленный объект query
 */
export function addCursorToQuery(query, cursor) {
    const updatedQuery = clearPaginationFromQuery(query);

    if (isValidCursor(cursor)) {
        updatedQuery.cursor = cursor;
    }

    return updatedQuery;
}

/**
 * Создает запись в истории поиска
 * @param {string} query - Поисковый запрос
 * @param {string} category - Категория поиска
 * @param {string} cursor - Курсор
 * @returns {Object} - Запись истории
 */
export function createSearchHistoryEntry(query, category, cursor) {
    return {
        query,
        category,
        cursor,
        timestamp: Date.now(),
    };
}

/**
 * Проверяет, является ли запись истории новой
 * @param {Object} newEntry - Новая запись
 * @param {Object} lastEntry - Последняя запись в истории
 * @returns {boolean} - Является ли запись новой
 */
export function isNewHistoryEntry(newEntry, lastEntry) {
    if (!lastEntry) return true;

    return (
        newEntry.query !== lastEntry.query ||
        newEntry.category !== lastEntry.category ||
        newEntry.cursor !== lastEntry.cursor
    );
}

/**
 * Получает информацию о текущей позиции в пагинации
 * @param {Object} pagination - Объект пагинации
 * @param {Array} searchHistory - История поиска
 * @returns {Object} - Информация о позиции
 */
export function getPaginationPosition(pagination, searchHistory = []) {
    return {
        hasNext: canGoNext(pagination),
        hasPrev: canGoPrev(pagination),
        currentCursor: pagination.current_cursor,
        nextCursor: pagination.next_cursor,
        prevCursor: pagination.prev_cursor,
        totalInHistory: searchHistory.length,
        currentIndex: searchHistory.length - 1,
    };
}
